import { useEffect, useRef, useState } from 'react';
import {
  clearRecycleBoot,
  isRecycleBootActive,
  readRecycleBootAt,
  recordBlackDuration,
  recycleBootRemainingMs,
} from '~/utils/recycleBoot';
import { noteChangeAction } from '~/services/diagnostics/changeSpy';

/**
 * Expõe ao render se estamos num recycle-boot (BLK) — e GARANTE que a UI normal volte quando o
 * teto expirar.
 *
 * <p>O timer é a parte essencial: sem ele, `isRecycleBootActive` só seria reavaliado quando
 * alguma outra coisa causasse render, e o preto poderia ficar preso além do teto. Aqui um
 * `setTimeout` do tamanho exato do restante desarma a janela sozinho.
 *
 * <p>A flag é lida UMA VEZ no mount: a decisão "este boot é um recycle" pertence ao boot, não
 * pode oscilar no meio da sessão.
 */
export function useRecycleBoot(): {
  isRecycleBoot: boolean;
  /** Consome a flag — chamar quando o playback realmente começar (1º frame). */
  finishRecycleBoot: () => void;
} {
  const markedAtRef = useRef<number | null>(null);
  if (markedAtRef.current === null) {
    markedAtRef.current = readRecycleBootAt();
  }
  const markedAt = markedAtRef.current;

  const [active, setActive] = useState(() =>
    isRecycleBootActive(markedAt, Date.now()),
  );

  useEffect(() => {
    if (!active) return;
    noteChangeAction('BLK_RECYCLE_BOOT', `black start markedAt=${markedAt}`);
    const remaining = recycleBootRemainingMs(markedAt, Date.now());
    // Teto de segurança: mesmo sem playback, a UI normal volta. Uma falha real (cache vazio,
    // sync quebrado) NÃO pode ficar escondida atrás de preto permanente.
    const timer = setTimeout(() => {
      noteChangeAction('BLK_RECYCLE_BOOT', 'cap expirado → UI normal');
      if (markedAt != null) recordBlackDuration(Date.now() - markedAt, 'cap');
      clearRecycleBoot();
      setActive(false);
    }, remaining);
    return () => clearTimeout(timer);
    // `markedAt` é estável (lido 1× no mount) → efeito roda no mount e ao desarmar.
  }, [active, markedAt]);

  const finishRecycleBoot = (): void => {
    if (!active) return;
    // MEDE A DURAÇÃO REAL DO PRETO (2026-07-28). O requisito do produto é 1-2s; o boot medido no
    // aparelho foi ~4,0s. Em vez de estimar, gravamos o número: `markedAt` é o instante do reload
    // e este ponto é o 1º frame que efetivamente tocou. Aparece no changeSpy como
    // `blackMs=NNNN`, então dá pra comparar reload-de-página (mesmo processo) com relaunch
    // (processo novo) sem cronômetro na mão.
    const blackMs = markedAt != null ? Date.now() - markedAt : null;
    noteChangeAction(
      'BLK_RECYCLE_BOOT',
      blackMs != null ? `blackMs=${blackMs}` : 'first frame',
    );
    // Ring persistente — o estado do espião é por boot e cada reciclagem sobrescrevia a medição
    // anterior (12 perdidas na bancada de 28/07).
    if (blackMs != null) recordBlackDuration(blackMs, 'frame');
    clearRecycleBoot();
    setActive(false);
  };

  return { isRecycleBoot: active, finishRecycleBoot };
}
